import { useState } from "react";
import { useNow } from "../hooks/useNow";
import { programmeringByMin, VENUE_LIST } from "../lib/data";
import { isCurrent, minutesNow } from "../lib/time";

export function StagesNow() {
  const now = useNow(15_000);
  const nowMin = minutesNow(now);
  const [openVenue, setOpenVenue] = useState<string | null>(null);

  return (
    <div className="space-y-2">
      {VENUE_LIST.map((venue) => {
        const acts = programmeringByMin.filter(
          (p) => p.locatie.venue === venue && !p.is_overleg,
        );
        const current = acts.find((p) =>
          isCurrent(p.vanMin, p.totMin, nowMin),
        );
        const next = acts.find((p) => p.vanMin != null && p.vanMin > nowMin);

        const elapsedMin =
          current && current.vanMin != null
            ? Math.max(0, nowMin - current.vanMin)
            : 0;
        const totalMin =
          current && current.vanMin != null && current.totMin != null
            ? current.totMin - current.vanMin
            : 0;
        const progress = totalMin > 0 ? Math.min(1, elapsedMin / totalMin) : 0;

        const isOpen = openVenue === venue;
        return (
          <div
            key={venue}
            className="card p-3 cursor-pointer"
            role="button"
            aria-expanded={isOpen}
            onClick={() => setOpenVenue(isOpen ? null : venue)}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <span
                  className={`w-1 h-1 rounded-full ${current ? "bg-ok" : "bg-zinc-600"}`}
                />
                <span className="eyebrow">{venue}</span>
              </div>
              {current && (
                <span className="pill bg-ok/15 text-ok">
                  <span className="w-1.5 h-1.5 rounded-full bg-ok animate-pulseSoft" />
                  Live
                </span>
              )}
            </div>
            {current ? (
              <>
                <div className="font-semibold text-zinc-100 text-base">
                  {current.has_aandacht && (
                    <span className="text-warn mr-1.5">⚠</span>
                  )}
                  {current.wat}
                </div>
                <div className="h-0.5 bg-white/[0.05] rounded-full overflow-hidden mt-2.5">
                  <div
                    className="h-full bg-ok transition-all duration-1000 ease-spring"
                    style={{ width: `${Math.round(progress * 100)}%` }}
                  />
                </div>
                <div className="text-2xs text-zinc-500 tabular mt-2 flex items-center justify-between">
                  <span>
                    {elapsedMin}/{totalMin} min · tot {current.tijd_tot}
                  </span>
                  {next && (
                    <span className="text-zinc-600 truncate ml-2">
                      → {next.tijd_van}
                    </span>
                  )}
                </div>
              </>
            ) : next ? (
              <>
                <div className="text-zinc-500 text-sm">— stil —</div>
                <div className="text-2xs text-zinc-600 mt-2">
                  Volgend: {next.wat}{" "}
                  <span className="tabular">{next.tijd_van}</span>
                </div>
              </>
            ) : (
              <div className="text-zinc-600 text-sm">— afgelopen —</div>
            )}
            {isOpen && (
              <ul className="mt-3 pt-2 border-t border-white/10 space-y-1">
                {acts.map((a) => {
                  const live = isCurrent(a.vanMin, a.totMin, nowMin);
                  const voorbij = a.totMin != null && nowMin >= a.totMin;
                  return (
                    <li
                      key={a.id}
                      className={`flex items-baseline gap-2 rounded px-1.5 py-1 text-sm ${
                        live
                          ? "bg-ok/10 text-zinc-100"
                          : voorbij
                            ? "text-zinc-600"
                            : "text-zinc-300"
                      }`}
                    >
                      <span className="font-mono tabular text-2xs shrink-0 w-[5.5rem]">
                        {a.tijd_van}–{a.tijd_tot}
                      </span>
                      <span className="truncate">
                        {a.has_aandacht && <span className="text-warn mr-1">⚠</span>}
                        {a.wat}
                      </span>
                      {live && (
                        <span className="ml-auto pill bg-ok/15 text-ok shrink-0">Live</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
